

import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useGuitarMatch } from '../context/GuitarMatchContext';
import { tutorialSteps } from '../services/tutorialData';
import { AppState, TutorialContext, TutorialStep } from '../contracts/scoring';

interface TutorialOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  forcedContext?: TutorialContext;
}

export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ isOpen, onClose, forcedContext }) => {
  const { lang, appState } = useGuitarMatch();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  const context = useMemo(() => {
      if (forcedContext) return forcedContext;
      return appState === AppState.RESULTS ? 'RESULTS' : 'STUDIO';
  }, [forcedContext, appState]);

  const steps = useMemo(() => tutorialSteps(lang, context), [lang, context]);

  const [placement, setPlacement] = useState({ 
      top: -9999, 
      left: 0, 
      arrowOffset: 0, 
      isTop: false, 
      isRight: false,
      opacity: 0
  });

  const timeoutsRef = useRef<number[]>([]);
  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(id => window.clearTimeout(id));
    timeoutsRef.current = [];
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    clearAllTimeouts();
    if (!isOpen) {
      setRect(null);
      setPlacement(p => ({ ...p, opacity: 0 }));
      return;
    }
    setCurrentStepIndex(0);
    const t = window.setTimeout(() => updateRect(0), 100);
    timeoutsRef.current.push(t);
    return () => clearAllTimeouts();
  }, [isOpen, lang, context]);

  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => updateRect(currentStepIndex);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isOpen, currentStepIndex, steps]);

  const updateRect = (index: number, attempt = 0) => {
    if (!steps[index]) return;
    const element = document.getElementById(steps[index].targetId);

    // FIX: Vérification si l'élément est visible (hauteur > 0)
    // S'il est masqué par CSS (ex: mode DUO désactivé), on passe à l'étape suivante
    if (!element || element.getBoundingClientRect().height < 2) {
      if (attempt < 4 && isOpen) {
        const retryTimeout = window.setTimeout(() => updateRect(index, attempt + 1), 250);
        timeoutsRef.current.push(retryTimeout);
      } else {
        // L'élément est vraiment absent ou masqué, on skip cette étape automatiquement
        if (index < steps.length - 1) {
            const nextIdx = index + 1;
            setCurrentStepIndex(nextIdx);
            updateRect(nextIdx);
        } else {
            onClose();
        }
      }
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

    const t = window.setTimeout(() => {
      if (!isOpen) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newRect = element.getBoundingClientRect();
          if (newRect.width > 0) {
            setRect(newRect);
          } else if (attempt < 8) {
             updateRect(index, attempt + 1);
          }
        });
      });
    }, 400);

    timeoutsRef.current.push(t);
  };

  useLayoutEffect(() => {
    if (!rect || !tooltipRef.current) return;
    
    const tooltip = tooltipRef.current;
    const { width, height } = tooltip.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const GAP = 24; 
    const MARGIN = 16;
    
    const currentStep = steps[currentStepIndex];
    const preferredPos = currentStep?.position || 'bottom';
    
    let left = 0;
    let top = 0;
    let isTop = false;
    let isRight = false;
    let arrowOffset = 0;

    if (preferredPos === 'right') {
        top = rect.top + (rect.height / 2) - (height / 2);
        left = rect.right + GAP;
        isRight = true;
        if (left + width > viewportW - MARGIN) {
            left = rect.left - width - GAP;
            isRight = false;
        }
    } else if (preferredPos === 'top' || preferredPos === 'bottom') {
        const targetCenterX = rect.left + (rect.width / 2);
        left = targetCenterX - (width / 2);

        if (left < MARGIN) left = MARGIN;
        if (left + width > viewportW - MARGIN) left = viewportW - width - MARGIN;
        
        arrowOffset = targetCenterX - left;
        arrowOffset = Math.max(20, Math.min(width - 20, arrowOffset));

        if (preferredPos === 'top') {
            top = rect.top - height - GAP;
            isTop = true;
            if (top < MARGIN) {
                top = rect.bottom + GAP;
                isTop = false;
            }
        } else {
            top = rect.bottom + GAP;
            isTop = false;
            if (top + height > viewportH - MARGIN) {
                top = rect.top - height - GAP;
                isTop = true;
            }
        }
    }

    if (top < MARGIN) top = MARGIN;
    if (top + height > viewportH - MARGIN) top = viewportH - height - MARGIN;

    setPlacement({ top, left, arrowOffset, isTop, isRight, opacity: 1 });

  }, [rect, currentStepIndex, steps]);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setPlacement(p => ({ ...p, opacity: 0 })); 
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      updateRect(nextIndex);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setPlacement(p => ({ ...p, opacity: 0 }));
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      updateRect(prevIndex);
    }
  };

  if (!isOpen || steps.length === 0) return null;

  const currentStep = steps[currentStepIndex];

  return createPortal(
    <div className="fixed inset-0 z-[20000] pointer-events-none font-sans">
      {rect && (
          <div 
            className="fixed transition-all duration-300 ease-out rounded-2xl"
            style={{
                top: rect.top - 8,
                left: rect.left - 8,
                width: rect.width + 16,
                height: rect.height + 16,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.85), 0 0 40px 10px rgba(255,255,255,0.3)',
                border: '2px solid rgba(255,255,255,0.8)',
                zIndex: 19998
            }}
          >
              <div className="absolute inset-0 rounded-2xl animate-pulse shadow-[inset_0_0_30px_rgba(255,255,255,0.4)]"></div>
          </div>
      )}

      {rect && (
          <div 
            ref={tooltipRef}
            className="fixed transition-all duration-300 ease-out flex flex-col items-center pointer-events-auto"
            style={{
                top: placement.top,
                left: placement.left,
                opacity: placement.opacity,
                zIndex: 19999
            }}
          > 
             <div 
                className="absolute w-0 h-0 border-[12px] border-transparent transition-all duration-300"
                style={{
                    left: placement.arrowOffset || (placement.isRight ? '-12px' : 'auto'),
                    right: (!placement.arrowOffset && !placement.isRight) ? '-12px' : 'auto',
                    marginLeft: placement.arrowOffset ? '-12px' : '0', 
                    top: placement.isTop ? '100%' : (placement.isRight ? '50%' : '-12px'),
                    marginTop: (placement.isRight || !placement.arrowOffset) ? '-12px' : '0',
                    borderTopColor: placement.isTop ? '#fff' : 'transparent',
                    borderBottomColor: (!placement.isTop && !placement.isRight) ? '#fff' : 'transparent',
                    borderRightColor: placement.isRight ? '#fff' : 'transparent',
                    opacity: 1
                }}
             />
            
            <div className="bg-white text-slate-900 p-8 rounded-[32px] max-w-sm w-[360px] relative border border-white shadow-[0_20px_80px_rgba(0,0,0,0.6)] max-h-[85vh] overflow-y-auto">
                <button 
                    onClick={onClose} 
                    className="absolute top-6 right-6 text-slate-400 hover:text-smart-accent text-[10px] font-black uppercase tracking-widest transition-colors"
                >
                    Passer
                </button>
                
                <div className="flex flex-col mb-4 pr-12">
                    <span className="text-[10px] font-black uppercase tracking-[0.4em] text-smart-accent mb-2">
                        {context === 'RECORDER' ? 'Leçons & Prises' : context === 'STUDIO' ? 'Mode Studio' : 'Analyses'}
                    </span>
                    <h3 className="text-3xl font-black text-slate-900 leading-tight italic tracking-tighter">{currentStep.title}</h3>
                </div>
                
                <p className="text-base text-slate-600 leading-relaxed mb-8 font-medium italic border-l-4 border-smart-accent/20 pl-4">
                    {currentStep.content}
                </p>
                
                <div className="flex justify-between items-center border-t border-slate-100 pt-6">
                    <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-3 py-1.5 rounded-full uppercase tracking-widest">
                        {currentStepIndex + 1} / {steps.length}
                    </span>
                    <div className="flex gap-3">
                        <button 
                            onClick={handlePrev} 
                            disabled={currentStepIndex === 0}
                            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-30 transition-all"
                        >
                            Précédent
                        </button>
                        <button 
                            onClick={handleNext} 
                            className="px-6 py-2 rounded-xl bg-smart-accent text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-xl hover:scale-105 transition-all"
                        >
                            {currentStepIndex === steps.length - 1 ? 'Terminer' : 'Suivant'}
                        </button>
                    </div>
                </div>
            </div>
          </div>
      )}
    </div>,
    document.body
  );
};